import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { marketingAccent, marketingButton, marketingType } from "../../marketing.type";
import {
  marketingAudienceCtasData,
  type AudienceCta,
} from "./marketingAudienceCtas.data";

export type Props = {
  onCtaClick: (cta: AudienceCta) => void;
  className?: string;
};

/**
 * The three ways in, as cards under the hero.
 *
 * Three cards side by side rather than the rows used further down the page,
 * because this is where somebody decides which of the three they are. Each
 * card is the whole argument for one audience: who it is for, what they get,
 * and the two things to do about it.
 *
 * The buttons are themed dark because the card is: the design gives the quieter
 * route an outline against the card and the form a filled button, which is the
 * dark theme's secondary and primary.
 */
export function MarketingAudienceCards({ onCtaClick, className }: Props) {
  return (
    <div
      data-testid="marketing-audience-cards"
      // One column on a phone and three on a desktop. On a tablet the first
      // card runs the full width and the other two share the row beneath.
      className={`grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-6 lg:grid-cols-3 lg:gap-8 xl:-mx-4 ${className ?? ""}`}
    >
      {marketingAudienceCtasData.map((audience, index) => (
        <div
          key={audience.key}
          className={`bg-brand-800 text-brand-50 rounded-[26px] p-6 flex flex-col gap-6 lg:gap-8 ${
            index === 0 ? "md:col-span-2 lg:col-span-1" : "md:min-h-[318px] lg:min-h-0"
          }`}
          data-testid={`marketing-audience-card-${audience.key}`}
        >
          <Type
            variant="caption"
            as="p"
            className={`${marketingType.cardEyebrow} ${marketingAccent} self-start max-w-none`}
          >
            {audience.title}
          </Type>
          {audience.heading && (
            // The cards sit under the page's h1 with no h2 before them, so
            // they are the second level, whatever size they are drawn at.
            <Type
              variant="h3"
              as="h2"
              className={`${marketingType.display} ${marketingType.displayWeight} -mb-2 lg:-my-[9px]`}
            >
              {audience.heading}
            </Type>
          )}
          {audience.description && (
            <Type variant="body2" className={`flex-1 max-w-none pb-2 lg:pb-0 ${marketingType.small}`}>
              {audience.description}
            </Type>
          )}
          <div className="flex flex-col gap-3 md:flex-row lg:flex-col">
            {audience.ctas.map((cta) => (
              <Button
                key={cta.label}
                as="a"
                href={cta.href}
                theme="dark"
                variant={cta.variant ?? "primary"}
                // The filled button is drawn 41px tall with a green edge;
                // the outlined one above it is 44px.
                className={`md:flex-1 md:min-w-0 md:px-0 lg:flex-none lg:px-2 ${
                  (cta.variant ?? "primary") === "primary"
                    ? "h-[41px] border-brand-700"
                    : "h-11"
                }`}
                typeClassName={marketingButton.compactTypeClassName}
                data-testid={`marketing-card-cta-${cta.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  onCtaClick(cta);
                }}
              >
                {cta.label}
              </Button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default MarketingAudienceCards;
